import React, { useEffect } from 'react';
import { Fade, Paper, Box, IconButton, Typography, styled, ClickAwayListener, useTheme, ThemeProvider, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../context/SidebarContext';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import checkmarxLogo from '../../assets/icons/scanners/checkmarx.png';
import wizLogo from '../../assets/icons/scanners/wiz.png';


import {
  Lock as ThreatModelIcon,
  Extension as SbomIcon,
  Analytics as ScaIcon,
  Key as SecretsIcon,
  Code as SastIcon,
  Security as DastIcon,
  Cloud as InfraIcon
} from '@mui/icons-material';

import { Notification } from '../../types/notifications';
import { useNotifications } from 'context/NotificationContext';
import { createRoot } from 'react-dom/client';
import { AnaSolutionDialogV2 } from 'components/SAST/NewScan/AnaSolutionDialogV2';
import { getUserData } from 'utils/encryption';
import { error, log } from 'utils/logger';
import { getNotifications } from 'services/api';
import { getAccessToken } from 'services/tokenService';

const StyledPaper = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '540px',
  maxHeight: 'calc(100vh - 96px)',
  borderRadius: '24px',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 17, 17, 0.95)' : 'rgba(255, 255, 255, 0.97)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
  backdropFilter: 'blur(20px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 12px 45px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08)'
    : '0 12px 45px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.04)',
  transform: 'translateY(0)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 16px 55px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 16px 55px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.08)'
  }
}));

const NotificationItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)',
  borderRadius: '16px',
  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'}`,
  
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.04)',
    transform: 'translateX(4px) scale(1.01)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 6px 16px rgba(0, 0, 0, 0.3)'
      : '0 6px 16px rgba(0, 0, 0, 0.08)',
    '&::before': {
      opacity: 1,
      transform: 'translateX(0)',
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '8px',
    top: '50%',
    transform: 'translateX(-8px)',
    width: '4px',
    height: '40px',
    borderRadius: '4px',
    background: `linear-gradient(to bottom, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    opacity: 0,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    marginTop: '-20px',
  }
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  maxHeight: '450px',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(0, 1.5),
  marginLeft: theme.spacing(-1.5),
  marginRight: theme.spacing(-1.5),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(0, 0, 0, 0.18)',
    },
  },
}));

export const getIconByType = (notification: Notification) => {
  switch (notification.scan_type?.toLowerCase()) {
    case 'threat-model':
    case 'threat_model':
      return <ThreatModelIcon />;
    case 'sbom':
      return <SbomIcon />;
    case 'sca':
      return <ScaIcon />;
    case 'secrets':
    case 'secret':
      return <SecretsIcon />;
    case 'sast':
      return <SastIcon />;
    case 'dast':
      return <DastIcon />;
    case 'infra':
    case 'infrastructure':
      return <InfraIcon />;
    case 'ana':
      return <img src="/images/ana_logo.png" alt="Ana Logo" style={{ width: 24, height: 24 }} />;
    case 'checkmarx':
      return <img src={checkmarxLogo} alt="Checkmarx Logo" style={{ width: 24, height: 24 }} />;
    case 'wiz':
      return <img src={wizLogo} alt="Wiz Logo" style={{ width: 24, height: 24 }} />;

    default:
      // If scan_type is not available, fallback to notification.type
      switch (notification.type?.toLowerCase()) {
        case 'fix_complete':
          return <ThreatModelIcon />;
        case 'refresh_complete':
          return <SbomIcon />;
        default:
          return <SbomIcon />;
      }
  }
};

const getNotificationMessage = (notification: Notification): string => {
  return notification.message;
};

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkAllAsRead: () => void;
  buttonRef?: React.RefObject<HTMLElement>;
  total: number;
  page: number;
  onPageChange: (page: number) => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  notifications,
  onMarkAllAsRead,
  buttonRef,
  total,
  page,
  onPageChange
}) => {
  const theme = useTheme();
  const { isOpen: isSidebarOpen } = useSidebar();
  const { markAsRead, updateNotifications } = useNotifications();
  const navigate = useNavigate();
  
  // Fetch notifications when panel is opened
  useEffect(() => {
    const fetchNotificationsData = async () => {
      if (isOpen) {
        try {
          const notificationResponse = await getNotifications(10, (page - 1) * 10);
          updateNotifications(notificationResponse.notifications, notificationResponse.total);
        } catch (err) {
          error('Failed to fetch notifications:', err);
        }
      }
    };
    
    fetchNotificationsData();
  }, [isOpen, page,updateNotifications]);

  const getTransform = () => {
    return `translateX(${isSidebarOpen ? '250px' : '80px'})`;
  };

  const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    
    // Handle future dates
    if (diffMs < 0) {
      const absDiffMs = Math.abs(diffMs);
      const minutes = Math.floor(absDiffMs / (1000 * 60));
      const hours = Math.floor(minutes / 60);
      
      if (minutes < 60) {
        return `in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
      } else if (hours < 24) {
        return `in ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
      } else {
        // For future dates more than a day away, show the date and time
        return formatDateAndTime(date);
      }
    }
    
    // Handle past dates
    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    
    if (minutes < 1) {
      return 'just now';
    } else if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      // For past dates more than a day old, show the date and time
      return formatDateAndTime(date);
    }
  };
  
  // Helper function to format date and time in a readable format
  const formatDateAndTime = (date: Date) => {
    // Format: "Jan 15, 2023 at 14:30"
    const options: Intl.DateTimeFormatOptions = { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return date.toLocaleDateString('en-US', options).replace(',', ' at');
  };

  if (!isOpen) return null;

  return createPortal(
    <Box sx={{
      position: 'fixed',
      left: 0,
      bottom: '10px',
      zIndex: theme.zIndex.drawer + 3,
      transform: getTransform(),
      transition: 'transform 0.2s ease-in-out',
      '@media (max-width: 600px)': {
        transform: 'translateX(16px)',
      }
    }}>
      <Fade in={isOpen}>
        <Box>
          <ClickAwayListener onClickAway={onClose}>
            <StyledPaper>
              <Box sx={{ p: 3 }}>
                {/* Header section */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  mt: 1
                }}>
                  <Typography variant="h5" sx={{ 
                    color: theme.palette.mode === 'dark' ? 'white' : 'black', 
                    fontWeight: 600,
                    letterSpacing: '-0.01em'
                  }}>
                    Notifications
                  </Typography>
                  <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      '&:hover': {
                        color: theme.palette.mode === 'dark' ? 'white' : 'black',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Actions section */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}>
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500,
                      letterSpacing: '0.05em',
                      fontSize: '0.75rem'
                    }}
                  >
                    RECENT ({notifications.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3 }}>
<Typography
                        variant="body2"
                        sx={{
                          color: '#6366f1',
                          cursor: 'pointer',
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          fontSize: '0.75rem',
                          '&:hover': { color: '#818cf8' },
                          transition: 'color 0.2s ease'
                        }}
                      onClick={async () => {
                        try {
                          await markAsRead(0);
                          onMarkAllAsRead();
                        } catch (err : any) {
                          error('Failed to mark all notifications as read:', err);
                        }
                      }}
                    >
                      MARK AS READ
                    </Typography>
                  </Box>
                </Box>

                {/* Notifications list */}
                <ScrollableContent>
                  {notifications.length === 0 ? (
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 6,
                      px: 3,
                      textAlign: 'center'
                    }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                          mb: 1,
                          fontWeight: 500
                        }}
                      >
                        No notifications yet
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                          maxWidth: '280px'
                        }}
                      >
                        When you run scans or receive alerts, they'll show up here
                      </Typography>
                    </Box>
                  ) : (
                    notifications.map((notification) => (
                      <NotificationItem
                        key={notification.id}
                        onClick={async () => {
                          try {
                            await markAsRead(notification.id);
                            // Update the local state for this specific notification
                            notifications.forEach(n => {
                              if (n.id === notification.id) {
                                n.is_read = 1;
                              }
                            });

                            // Handle routing based on scan type
                            if (notification.data?.scan_id) {
                              const scanType = notification.scan_type?.toLowerCase();
                              let route = '';

                              switch (scanType) {
                                case 'sbom':
                                  route = `/sbom/scan/${notification.data.scan_id}`;
                                  break;
                                case 'sca':
                                  route = `/sca/scan/${notification.data.scan_id}`;
                                  break;
                                case 'secrets':
                                case 'secret':
                                  route = `/secrets/scan/${notification.data.scan_id}`;
                                  break;
                                case 'api':
                                case 'api-security':
                                  route = `/api-security/scan/${notification.data.scan_id}`;
                                  break;
                                case 'threat-model':
                                case 'threat_model':
                                  route = `/threat-modeling/scan/${notification.data.scan_id}`;
                                  break;
                                case 'sast':
                                  route = `/sast/scan/${notification.data.scan_id}`;
                                  break;
                                case 'dast':
                                  route = `/dast/scan/${notification.data.scan_id}`;
                                  break;
                                case 'infra':
                                case 'infrastructure':
                                  route = `/infra/scan/${notification.data.scan_id}`;
                                  break;
                                case 'ana':
                                  // Check if user is authenticated before opening ANA dialog
                                  const authToken = getAccessToken();
                                  const user = getUserData();
                                  
                                  if (!authToken || !user) {
                                    // User is not authenticated, redirect to login
                                    log('User not authenticated, redirecting to login');
                                    navigate('/login');
                                    onClose();
                                    return;
                                  }
                                  
                                  // Create container for Ana dialog
                                  let anaContainer = document.getElementById('ana-dialog-container');
                                  if (!anaContainer) {
                                    anaContainer = document.createElement('div');
                                    anaContainer.id = 'ana-dialog-container';
                                    document.body.appendChild(anaContainer);
                                  }

                                  // Create root and render AnaSolutionDialogV2 with ThemeProvider
                                  const anaRoot = createRoot(anaContainer);
                                  anaRoot.render(
                                    React.createElement(
                                      ThemeProvider,
                                      { theme: theme },
                                      React.createElement(AnaSolutionDialogV2, {
                                        open: true,
                                        onClose: () => {
                                          anaRoot.unmount();
                                          anaContainer?.remove();
                                        },
                                        finding_id: notification.data.finding_id || 0
                                      })
                                    )
                                  );
                                  return; // Don't navigate for ana notifications
                              }

                              if (route) {
                                navigate(route);
                                onClose(); // Close the notification panel after navigation
                              }
                            }
                          } catch (err : any) {
                            error('Failed to mark notification as read:', err);
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              backgroundColor: 'rgba(96, 165, 250, 0.1)',
                            }}
                          >
                            {React.cloneElement(getIconByType(notification), {
                              sx: { width: 22, height: 22, opacity: 0.9 }
                            })}
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                              <Typography
                                sx={{
                                  color: notification.is_read
                                    ? theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'
                                    : theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {notification.scan_type?.toUpperCase()}
                                {!notification.is_read && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      backgroundColor: '#4f46e5',
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </Typography>
                              <Typography
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                  fontSize: '0.75rem',
                                }}
                              >
                                {formatTimestamp(notification.created_at)}
                              </Typography>
                            </Box>
                            <Tooltip
                              title={getNotificationMessage(notification).length > 50 ? getNotificationMessage(notification) : ''}
                              placement="top"
                            >
                              <Typography
                                sx={{
                                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                                  fontSize: '0.8125rem',
                                  lineHeight: 1.4,
                                  maxWidth: '100%'
                                }}
                              >
                                {getNotificationMessage(notification).length > 50 ? `${getNotificationMessage(notification).substring(0, 50)}...` : getNotificationMessage(notification)}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>
                      </NotificationItem>
                    ))
                  )}
                </ScrollableContent>
                {/* Pagination section */}
                {notifications.length > 0 && (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                    pb: 2,
                    px: 2,
                    borderTop: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                    pt: 2.5
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)',
                      }}
                    >
                      Showing {notifications.length} of {total}
                    </Typography>
                    {total > notifications.length && (
                      <Typography
                        variant="body2"
                        sx={{
color: '#6366f1',
                        cursor: 'pointer',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.08)',
                        transition: 'all 0.2s ease',
                        '&:hover': { 
                          color: '#818cf8',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.15)'
                        }
                        }}
                        onClick={() => onPageChange(page + 1)}
                      >
                        Load More
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </StyledPaper>
          </ClickAwayListener>
        </Box>
      </Fade>
    </Box>,
    document.body
  );
};